import { useEffect, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import {
  isWarmupOnly,
  mailboxRole,
  type Mailbox,
  type MailboxRole,
} from '@revbase/shared';
import {
  Mail,
  Plus,
  Inbox as InboxIcon,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { db } from '../lib/firebase.js';
import { useAuth } from '../hooks/useAuth.js';
import { api } from '../lib/api.js';
import { Button, Badge, PageHeader, EmptyState } from '../components/ui.js';

interface MailboxRow extends Mailbox {
  id: string;
}

/** Map a mailbox status to a semantic badge variant. */
function statusVariant(status: string): 'good' | 'warn' | 'bad' | 'neutral' {
  switch (status) {
    case 'active':
      return 'good';
    case 'warming':
    case 'paused':
      return 'warn';
    case 'error':
    case 'disconnected':
      return 'bad';
    default:
      return 'neutral';
  }
}

export function Mailboxes() {
  const { tenantId } = useAuth();
  const [mailboxes, setMailboxes] = useState<MailboxRow[]>([]);
  // Tracks which connect action is mid-redirect so only that button shows busy.
  const [connecting, setConnecting] = useState<MailboxRole | null>(null);
  // Id of the mailbox currently being reconnected (for the busy state).
  const [reconnecting, setReconnecting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(
      collection(db, `tenants/${tenantId}/mailboxes`),
      (snap) => {
        setMailboxes(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Mailbox) })),
        );
      },
    );
  }, [tenantId]);

  const connect = async (role: MailboxRole) => {
    setConnecting(role);
    setError(null);
    try {
      const { authUrl } = await api.startMailboxConnect({
        returnPath: '/mailboxes',
        role,
      });
      window.location.href = authUrl;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start connection');
      setConnecting(null);
    }
  };

  // Reconnect refreshes an existing mailbox in place to grant the current Gmail
  // permissions (for example after the warmup scope was added). Same account
  // required; the server verifies it and updates the record without resetting
  // warmup or ramp progress.
  const reconnect = async (m: MailboxRow) => {
    setReconnecting(m.id);
    setError(null);
    try {
      const { authUrl } = await api.startMailboxConnect({
        returnPath: '/mailboxes',
        role: mailboxRole(m),
        mailboxId: m.id,
      });
      window.location.href = authUrl;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start reconnect');
      setReconnecting(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Mail}
        eyebrow="Sending"
        title="Mailboxes"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              icon={Plus}
              onClick={() => void connect('sending')}
              disabled={connecting !== null}
            >
              {connecting === 'sending'
                ? 'Redirecting…'
                : 'Connect sending mailbox'}
            </Button>
            <Button
              variant="ghost"
              icon={InboxIcon}
              onClick={() => void connect('warmup_only')}
              disabled={connecting !== null}
            >
              {connecting === 'warmup_only'
                ? 'Redirecting…'
                : 'Connect warmup inbox'}
            </Button>
          </div>
        }
      />

      <p className="text-slate-600">
        Sending mailboxes must be Google Workspace accounts on your own domain.
        Free{' '}
        <code className="font-mono text-sm text-slate-900">@gmail.com</code>{' '}
        accounts are rejected for sending. A warmup inbox is different: it joins
        the warmup pool as a peer only, never sends campaigns, and so a free
        Gmail is allowed there.
      </p>

      {error && (
        <div
          role="alert"
          className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          <AlertCircle className="h-4 w-4 shrink-0" strokeWidth={2} aria-hidden />
          {error}
        </div>
      )}

      {mailboxes.length === 0 ? (
        <EmptyState
          icon={Mail}
          title="No mailbox connected yet"
          description="Connect a Google Workspace mailbox on your own domain to start sending campaigns."
          action={
            <Button
              icon={Plus}
              onClick={() => void connect('sending')}
              disabled={connecting !== null}
            >
              {connecting === 'sending'
                ? 'Redirecting…'
                : 'Connect sending mailbox'}
            </Button>
          }
        />
      ) : (
        <div className="card overflow-x-auto p-0">
          <table className="data-table">
            <thead>
              <tr>
                <th>Mailbox</th>
                <th>Status</th>
                <th>Ramp</th>
                <th>Send window</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {mailboxes.map((m) => (
                <tr key={m.id}>
                  <td className="font-mono text-slate-900">{m.email}</td>
                  <td>
                    <Badge variant={statusVariant(m.status)} dot>
                      {m.status}
                    </Badge>
                  </td>
                  <td className="text-xs text-slate-500">
                    {isWarmupOnly(m) ? (
                      <span className="flex items-center gap-2">
                        <Badge variant="info">Warmup only</Badge>
                        <span>does not send campaigns</span>
                      </span>
                    ) : (
                      <span>
                        ramp day {m.rampDay} · {m.sentToday}/{m.dailyCap} sent
                        today
                      </span>
                    )}
                  </td>
                  <td className="font-mono text-xs text-slate-400">
                    <div>
                      {m.sendWindow.start}-{m.sendWindow.end} {m.sendWindow.tz}
                    </div>
                    {m.health.lastError && (
                      <div className="mt-1 text-red-600">
                        {m.health.lastError}
                      </div>
                    )}
                  </td>
                  <td className="text-right">
                    <Button
                      variant="ghost"
                      icon={RefreshCw}
                      onClick={() => void reconnect(m)}
                      disabled={reconnecting !== null || connecting !== null}
                      className="shrink-0"
                      title="Re-grant Gmail permissions for this mailbox (same account). Does not reset warmup or ramp."
                    >
                      {reconnecting === m.id ? 'Redirecting…' : 'Reconnect'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
